<template>
  <div class="d-flex flex-column">
    <Transition>
      <div v-if="!file" class="d-flex flex-column">
        <div>
          <FileUpload
            :text="fileName"
            outline
            :accept="asset.extensions.join(', ')"
            @change="handleFileUpload"
          />
          Max {{ maxFileSizeMb }} MB
        </div>
        <div>
          <span class="link my-2 toggle-link" @click="toggleTextInput"
            ><em>Or use existing file URL</em></span
          >
        </div>
        <TextField
          v-if="manualInput"
          v-model="existingURL"
          no-label
          placeholder="Existing File URL"
        />
      </div>
      <div v-else class="d-flex align-items-end mb-2">
        <ProgressBar :progress="progress" :file="file" />
        <div class="text-danger cancel-upload ml-2" @click="cancelUpload">
          <i class="fad fa-times-circle mr-1"></i>
          {{ cancelText }}
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import API from "serviceshift-ui/api-client";
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

import FileUpload from "@/components/FileUpload.vue";
import ProgressBar from "@/components/ProgressBar.vue";
import TextField from "@/components/TextField.vue";
import Transition from "@/components/Transition.vue";
import CONSTANTS from "@/Constants.js";

const { MEGABYTE, MAX_ASSET_FILE_SIZE_MB } = CONSTANTS;

const props = defineProps({
  asset: {
    type: Object,
    default: null
  },
  links: {
    type: Object,
    default: null
  }
});
const emit = defineEmits(["upload-complete", "upload-canceled"]);

const store = useStore();
const existingURL = ref("");
const file = ref<any>(null);
const manualInput = ref(false);
const progress = ref(0);
const cancelToken = ref<any>(null);

const fileName = computed(() => {
  if (!file.value) {
    return "Browse...";
  }
  return file.value.name;
});

const cancelText = computed(() => {
  return progress.value < 100 ? "Cancel Upload" : "Remove File";
});

const maxFileSizeMb = computed(() => {
  return MAX_ASSET_FILE_SIZE_MB;
});

watch(existingURL, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    if (newValue) {
      emit("upload-complete", newValue);
    } else {
      emit("upload-canceled", props.asset.name);
    }
  }
});

function cancelUpload() {
  file.value = null;
  progress.value = 0;
  cancelToken.value.cancel("Upload canceled");
  emit("upload-canceled", props.asset.name);
}

async function handleFileUpload(e) {
  window.onbeforeunload = function (e) {
    e.preventDefault();
    e.returnValue = "";
  };
  file.value = e.target.files[0];

  if (file.value.size > MAX_ASSET_FILE_SIZE_MB * MEGABYTE) {
    window.notify.error(
      "File size of " +
        Math.round((file.value.size / MEGABYTE) * 10) / 10 +
        " MB is too large. Please ensure file size is less than " +
        MAX_ASSET_FILE_SIZE_MB +
        " MB."
    );
    file.value = null;
    return;
  }
  try {
    const res = await store.dispatch("getS3UploadURL", {
      link: props.links.file_upload,
      filename: file.value.name
    });
    cancelToken.value = API.axios.CancelToken.source();
    const s3Key = await store.dispatch("s3Upload", {
      data: res.data,
      file: file.value,
      body: {
        onUploadProgress: handleUploadProgress,
        cancelToken: cancelToken.value.token
      }
    });
    emit("upload-complete", s3Key);
  } catch (e: any) {
    if (e.message !== "Upload canceled") {
      window.notify.error(e);
    }
  }
}

function handleUploadProgress(progressEvent) {
  progress.value = (progressEvent.loaded / progressEvent.total) * 100;
}

function toggleTextInput() {
  manualInput.value = !manualInput.value;
}
</script>

<style lang="scss" scoped>
.toggle-link {
  width: 135px;
}
.cancel-upload {
  cursor: pointer;
  white-space: nowrap;
  margin-bottom: -7px;
}
</style>
